import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Paper, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import { Button, Grid, TextInput, Modal, Select, Icon, DateTimeInput, Typography, IconButton } from '@ts/storybook';
import { WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { getDictionaries } from './IndicatorType/utils';
import { IForm, CStatusIndicator, IStatusIndicator } from '@ts/ui-models';

const setTimestampValue = 'Moment of last event';
const mandatoryFields = ['type', 'name', 'possibleValues', 'operation', 'id', 'logicalId'] as const;

type Variant = 'operation' | 'operator';

interface IOperation {
  type: string;
  value: any;
}

export interface IIndicator extends IStatusIndicator {
  operation: IOperation;
}

interface OpsDictEntry {
  value: string;
  display: string;
}

interface Dictionaries {
  opsDict: Record<string, OpsDictEntry>;
  typesDict: Record<string, string[]>;
}

interface IAdvancedStatusInputProps extends IForm {
  statesList: IIndicator[];
  onChangeList: (list: IIndicator[]) => void;
  variant?: Variant;
  constraintEntity?: string;
  constraintEntityId?: string;
  title?: string;
  align?: string;
  isAdvancedSearch?: boolean;
}

const AdvancedStatusInput: React.FC<IAdvancedStatusInputProps> = props => {
  const { t } = useTranslation();
  const { mode, statesList, onChangeList, variant = 'operation', title: titleProp, isAdvancedSearch, ...rest } = props;

  const [title] = useState<string | undefined>(titleProp);
  const [dictionaries, setDictionaries] = useState<Dictionaries | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const { opsDict, typesDict } = getDictionaries(variant);
    // @ts-expect-error tipos de utils algo lajos
    setDictionaries({ opsDict, typesDict });
  }, [variant]);

  if (!dictionaries) return <p>Initializing...</p>;

  const { opsDict, typesDict } = dictionaries;

  // ---------- Helpers comunes ----------

  const getIndicatorAllowedOps = (indicator: IIndicator): string[] => typesDict[indicator.type] ?? [];

  const normalizeIncomingIndicator = (raw: any): IIndicator => {
    const base: any = typeof raw?.toJS === 'function' ? raw.toJS() : raw;

    const picked = _.pick(base, mandatoryFields as unknown as string[]);

    const operation: IOperation = picked.operation ?? {
      type: '',
      value: '',
    };

    return {
      ...(picked as IStatusIndicator),
      operation,
    };
  };

  const handleOperationChange = (indIdx: number, value: any, field: 'type' | 'value') => {
    const newList = statesList.map((ind, idx) =>
      idx === indIdx
        ? {
            ...ind,
            operation: {
              ...ind.operation,
              [field]: value,
            },
          }
        : ind
    );
    onChangeList(newList);
  };

  const handleDeleteIndicator = (idx: number) => {
    const newList = statesList.filter((_, i) => i !== idx);
    onChangeList(newList);
  };

  const addIndicator = (rawIndicator: CStatusIndicator | null) => {
    if (!rawIndicator) {
      setShowModal(false);
      return;
    }

    let indicator = normalizeIncomingIndicator(rawIndicator);

    // Si no tiene operation.type, ponemos el primero permitido
    if (!indicator.operation.type) {
      const options = getIndicatorAllowedOps(indicator);
      const firstOpKey = options[0];
      if (firstOpKey && opsDict[firstOpKey]) {
        indicator = {
          ...indicator,
          operation: {
            ...indicator.operation,
            type: opsDict[firstOpKey].value,
          },
        };
      }
    }

    const newList = [...statesList, indicator];
    onChangeList(newList);
    setShowModal(false);
  };

  // ---------- Vista: modo lectura ----------

  const renderViewMode = () => {
    const indicators = statesList ?? [];

    const rows = indicators.map((indicator, idx) => {
      const type = indicator.operation.type;
      const value = indicator.operation.value;

      const typeDisplay = t(opsDict[type].display);
      let valueDisplay = value;
      if (type === 'setTimestamp') valueDisplay = t(setTimestampValue);

      return (
        <TableRow key={indicator.logicalId ?? indicator.id ?? idx}>
          <TableCell>{indicator.name}</TableCell>
          <TableCell>{typeDisplay}</TableCell>
          <TableCell>{valueDisplay}</TableCell>
        </TableRow>
      );
    });

    return (
      <div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Indicator')}</TableCell>
              <TableCell>{t('Operation')}</TableCell>
              <TableCell>{t('Value')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <br />
      </div>
    );
  };

  // ---------- Vista: modo edición / new ----------

  const renderOpTypeInput = (indicator: IIndicator, indIdx: number) => {
    const options = getIndicatorAllowedOps(indicator);

    let menuItems =
      options.map(pv => {
        const { value, display } = opsDict[pv];
        return { value, label: display };
      }) ?? [];

    if (isAdvancedSearch) {
      menuItems = [{ value: 'eq', label: '=' }];
    }

    const defaultValue = options[0] ? opsDict[options[0]]?.value : '';
    const value = indicator.operation.type || defaultValue;

    return (
      <Select
        key={indIdx}
        value={value}
        onChange={val => handleOperationChange(indIdx, val, 'type')}
        sx={{ marginTop: '0!important' }}
        options={menuItems}
      />
    );
  };

  const renderOpValueInput = (indicator: IIndicator, indIdx: number) => {
    const { type, possibleValues = [] } = indicator;
    const opValue = indicator.operation.value;

    switch (type) {
      case 'list': {
        let menuItems = [{ value: '', label: 'Select a value' }];

        menuItems = menuItems.concat(possibleValues.map(pv => ({ value: pv, label: pv })));

        return (
          <Select
            key={indIdx}
            value={opValue || ''}
            onChange={value => handleOperationChange(indIdx, value, 'value')}
            options={menuItems}
          />
        );
      }
      case 'string':
        return (
          <TextInput
            key={indIdx}
            label=""
            placeholder="Value"
            value={opValue}
            onChangeValue={value => handleOperationChange(indIdx, value, 'value')}
          />
        );
      case 'integer':
      case 'float':
        return (
          <TextInput
            type="number"
            key={indIdx}
            label=""
            placeholder="Value"
            value={opValue}
            onChangeValue={value => handleOperationChange(indIdx, value, 'value')}
          />
        );
      case 'timestamp':
      case 'date':
      case 'time':
        return <DateTimeInput key={indIdx} value={opValue} onChange={value => handleOperationChange(indIdx, value, 'value')} />;
      default:
        return null;
    }
  };

  const renderEditRows = () =>
    statesList.map((indicator, indIdx) => (
      <TableRow key={indicator.logicalId ?? indicator.id ?? indIdx}>
        <TableCell>{indicator.name}</TableCell>
        <TableCell>{renderOpTypeInput(indicator, indIdx)}</TableCell>
        <TableCell>{renderOpValueInput(indicator, indIdx)}</TableCell>
        <TableCell>
          <IconButton onClick={() => handleDeleteIndicator(indIdx)}>
            <Icon icon="action-delete" />
          </IconButton>
        </TableCell>
      </TableRow>
    ));

  const renderModal = () => (
    <Modal withHeader disabled title="Add possible state" open={showModal} onClose={() => setShowModal(false)}>
      <Grid container spacing={1} className="w-[500px]">
        <WrapperEntitySelector
          multiple={false}
          onChangeEntity={v => addIndicator(v as CStatusIndicator)}
          searchComponent={generateSearchEntitySelector('statusIndicator')}
        />
      </Grid>
    </Modal>
  );

  const renderEditMode = () => (
    <Grid container spacing={1}>
      {renderModal()}
      <Grid size={{ xs: 12 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Indicator')}</TableCell>
              <TableCell>{t('Operation')}</TableCell>
              <TableCell>{t('Value')}</TableCell>
              <TableCell>{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderEditRows()}</TableBody>
        </Table>
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Button fullWidth variant="text" onClick={() => setShowModal(true)} label="Add status indicator" />
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    if (mode === 'view') return renderViewMode();
    if (mode === 'edit' || mode === 'new') return renderEditMode();
    return null;
  };

  return (
    <Paper className="p-2">
      <Typography variant="h6">{title ? t(title) : t('Change status')}</Typography>
      {renderContent()}
    </Paper>
  );
};

export default AdvancedStatusInput;
